import React, { useState, useEffect, useRef, useCallback } from 'react'
import styled, { css } from 'styled-components'
import textFit from 'textfit'
import { boldWords, UIColorTheme } from 'global'
import { ICard } from 'models'
import { Box, IBoxProps, Text, Flex } from 'components'

interface IBarProps extends IBoxProps, UIColorTheme {}

const Bar = styled(Box)<IBarProps>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 30px;
  border-radius: 15px;
  overflow: hidden;
  ${({ uiColorTheme }) =>
    css`
      border: 2px solid ${uiColorTheme === 'dark' ? '#000' : '#fff'};
    `}
`

interface IBarInnerProps extends IBarProps {
  percentage: number
}

const BarInner = styled(Box)<IBarInnerProps>`
  height: 30px;
  background-color: rgb(255, 255, 255, 0.5);
  border-radius: 15px;
  ${({ uiColorTheme }) =>
    uiColorTheme === 'dark' &&
    css`
      background-color: rgb(0, 0, 0, 0.5);
    `}
  ${({ percentage }) =>
    css`
      margin-right: ${percentage}%;
    `}
`

const LabelText = styled(Text)`
  color: #fff;
  font-size: 14px;
  position: absolute;
  top: 50%;
  right: 13px;
  transform: translateY(-50%);
  text-align: right;
  ${({ uiColorTheme }) =>
    uiColorTheme === 'dark' &&
    css`
      color: #000;
    `}
`

interface ITouchResultCard  // removes require from 'ways'
  extends Required<Omit<ICard, 'ways'>>,
    Pick<ICard, 'ways'>,
    UIColorTheme {
  onLoaded: () => void
  hasTip: boolean
}

const speedMap = {
  'slow motion tease': 'and a',
  'steady medium pace': 'at a',
}

export const TouchResultCard = ({
  uiColorTheme = 'light',
  category,
  ways,
  speed,
  pressure,
  onLoaded,
  hasTip,
}: ITouchResultCard) => {
  const ref = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLParagraphElement>(null)
  const [textContent, setTextContent] = useState<JSX.Element | null>(null)
  const [opacity, setOpacity] = useState(0)
  const loadedCb = useCallback(onLoaded, [])

  const howILikeIt = () => (
    <>
      I like my <b>{category}</b> touched with <b>{pressure.label}</b>,{' '}
      {speedMap[speed.label.toLowerCase()]} <b>{speed.label}</b>.
    </>
  )

  const waysILikeIt = useCallback(
    (w: string[]) => (
      <>
        The ways I like my <b>{category}</b> touched are {boldWords(w)}.
      </>
    ),
    [category]
  )

  const content = () => {
    return ways ? (
      <>
        {howILikeIt()} {waysILikeIt(ways)}
      </>
    ) : (
      <>{howILikeIt()}</>
    )
  }

  useEffect(() => {
    if (ref.current) {
      if (!textContent && ways && ways.length > 2) {
        setTextContent(waysILikeIt(ways))
      } else {
        textFit(contentRef.current, {
          minFontSize: 8,
          maxFontSize: 28,
          multiLine: true,
        })
        setOpacity(1)
        loadedCb()
      }
    }
  }, [textContent, setTextContent, setOpacity, ways, waysILikeIt, loadedCb])

  return (
    <Flex
      ref={ref}
      opacity={opacity}
      flexDirection='column'
      flex='1 0 auto'
      justifyContent='space-between'
    >
      <Text
        ref={contentRef}
        // textFit needs a height to vertically scale.
        height={hasTip ? '150px' : '225px'}
        lineHeight='130%'
        color={uiColorTheme === 'light' ? '#fff' : '#000'}
      >
        {textContent ? textContent : content()}
      </Text>

      <Flex flexDirection='column'>
        <Box position='relative' margin='8px 0'>
          <LabelText small uiColorTheme={uiColorTheme}>
            {pressure.label}
          </LabelText>
          <Bar uiColorTheme={uiColorTheme}>
            <BarInner percentage={100 - Number(pressure.value)} />
          </Bar>
        </Box>
        <Box position='relative' margin='8px 0'>
          <LabelText small uiColorTheme={uiColorTheme}>
            {speed.label}
          </LabelText>
          <Bar uiColorTheme={uiColorTheme}>
            <BarInner percentage={100 - Number(speed.value)} />
          </Bar>
        </Box>
      </Flex>
    </Flex>
  )
}
